import { Injectable, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';
import {
  CORE_MODULES,
  MODULE_CATALOG,
  type Entitlements,
  type FeatureKey,
} from '@core/models/platform.model';

/**
 * What the signed-in user's school may use (plan + add-ons), from
 * `GET /auth/entitlements`. Drives the sidebar, the dashboard widgets and the
 * `featureGuard`. Purely cosmetic — the API gates the same modules (403
 * MODULE_NOT_IN_PLAN) — so a failed load fails OPEN (every live module) rather
 * than locking a paying school out of its portal on a network blip.
 *
 * Loaded next to every `BrandingService.loadFromAuth()` (session restore, login,
 * impersonation start); cleared on logout / impersonation exit.
 */
@Injectable({ providedIn: 'root' })
export class EntitlementService {
  private readonly api = inject(ApiService);

  private readonly _modules = signal<ReadonlySet<FeatureKey>>(new Set());
  private readonly _loaded = signal(false);
  private readonly _planLabel = signal('');
  private readonly _isCustom = signal(false);
  private inFlight: Promise<void> | null = null;
  // Bumped by every load()/clear() so a stale response never overwrites newer state.
  private generation = 0;

  /** Every enabled module (core included) once loaded. */
  readonly modules = this._modules.asReadonly();
  readonly loaded = this._loaded.asReadonly();
  /** e.g. "Basic" or "Basic + Custom" ('' until loaded). */
  readonly planLabel = this._planLabel.asReadonly();
  /** True when the school carries priced add-ons. */
  readonly isCustom = this._isCustom.asReadonly();

  /**
   * True when the school has `key`. Core modules are always true; any other
   * module is false until entitlements have loaded. Signal-reactive, so it can
   * be used inside computed()s / templates.
   */
  has(key: FeatureKey): boolean {
    if (CORE_MODULES.has(key)) return true;
    return this._loaded() && this._modules().has(key);
  }

  /** (Re)load from the API. Concurrent callers share the newest request. */
  load(): Promise<void> {
    const gen = ++this.generation;
    const p = firstValueFrom(this.api.get<Entitlements>('/auth/entitlements'))
      .then((e) => {
        if (gen !== this.generation) return;
        this._modules.set(new Set(e.modules));
        this._planLabel.set(e.planName);
        this._isCustom.set(e.isCustom);
        this._loaded.set(true);
      })
      .catch(() => {
        if (gen !== this.generation) return;
        // Fail open: the server still enforces the plan.
        this._modules.set(
          new Set(MODULE_CATALOG.filter((m) => m.status === 'live').map((m) => m.key)),
        );
        this._planLabel.set('');
        this._isCustom.set(false);
        this._loaded.set(true);
      })
      .finally(() => {
        if (gen === this.generation) this.inFlight = null;
      });
    this.inFlight = p;
    return p;
  }

  /** Resolve once entitlements are loaded — starting a load if none is in flight. */
  ensureLoaded(): Promise<void> {
    if (this._loaded()) return Promise.resolve();
    return this.inFlight ?? this.load();
  }

  /** Forget everything (logout / impersonation exit). */
  clear(): void {
    this.generation++;
    this.inFlight = null;
    this._modules.set(new Set());
    this._planLabel.set('');
    this._isCustom.set(false);
    this._loaded.set(false);
  }
}
